import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, ExternalLink, ShoppingBag } from 'lucide-react';
import { clientsApi, ordersApi } from '../api/cms';
import { defaultClients, defaultOrders } from '../api/defaults';
import type { Client, ClientService, Order } from '../types';
import { useLang } from '../context/LangContext';

const PROD_TYPE_LABELS: Record<string, string> = {
  hosting: '☁ Hosting', webdesign: '🖥 Web Design', callcenter: '🎧 Call Center',
  voip: '📞 VoIP', crm: '📊 CRM', hardware: '🖱 Hardware', other: '📦 Altele',
};

const emptyClient: Omit<Client, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '', company: '', cui: '', email: '', phone: '', address: '',
  status: 'active', services: [], orderIds: [], totalValue: 0, currency: 'EUR', notes: '',
};

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [form, setForm] = useState<Omit<Client, 'id' | 'createdAt' | 'updatedAt'>>(emptyClient);
  const [client, setClient] = useState<Client | null>(null);
  const [clientOrders, setClientOrders] = useState<Order[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<'info' | 'services' | 'orders'>('info');

  useEffect(() => {
    ordersApi.list().catch((_e: unknown) => defaultOrders).then(orders => {
      if (!isNew) setClientOrders(orders.filter(o => o.clientId === id));
    });
    if (!isNew) {
      clientsApi.get(id!).then(c => {
        setClient(c);
        setForm({ name: c.name, company: c.company, cui: c.cui, email: c.email, phone: c.phone, address: c.address, status: c.status, services: [...c.services], orderIds: c.orderIds, totalValue: c.totalValue, currency: c.currency, notes: c.notes });
      }).catch((_e: unknown) => {
        const c = defaultClients.find(x => x.id === id);
        if (c) {
          setClient(c);
          setForm({ name: c.name, company: c.company, cui: c.cui, email: c.email, phone: c.phone, address: c.address, status: c.status, services: [...c.services], orderIds: c.orderIds, totalValue: c.totalValue, currency: c.currency, notes: c.notes });
          setClientOrders(defaultOrders.filter(o => o.clientId === id));
        }
      });
    }
  }, [id, isNew]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }));

  const updateService = (i: number, k: keyof ClientService, v: string) =>
    setForm(f => ({ ...f, services: f.services.map((s, j) => j === i ? { ...s, [k]: v } : s) }));

  const addService = () => setForm(f => ({
    ...f, services: [...f.services, { productId: '', productName: '', productType: 'webdesign', startDate: new Date().toISOString().split('T')[0], status: 'active', monthlyValue: 0, currency: 'EUR', notes: '' }]
  }));

  const removeService = (i: number) => setForm(f => ({ ...f, services: f.services.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await clientsApi.create(form);
      else await clientsApi.update(id!, form);
      setSaved(true);
      setTimeout(() => navigate('/clients'), 1000);
    } catch { setSaving(false); }
  };

  const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input {...props} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
  );

  const ORDER_STATUS_COLORS: Record<string, string> = {
    pending: 'text-yellow-400', confirmed: 'text-blue-400',
    in_progress: 'text-purple-400', completed: 'text-green-400', cancelled: 'text-red-400',
  };
  const ORDER_STATUS_RO: Record<string, string> = {
    pending: 'În așteptare', confirmed: 'Confirmat', in_progress: 'În execuție', completed: 'Finalizat', cancelled: 'Anulat'
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/clients')} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-all"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{isNew ? t('Client nou', 'New Client') : form.company || form.name || '...'}</h1>
          {!isNew && client && <div className="text-xs text-white/30 mt-1">{t('Client din', 'Client since')} {client.createdAt} · ID: {client.id}</div>}
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-lg">✓ {t('Salvat', 'Saved')}</span>}
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            <Save size={16} /> {saving ? t('Se salvează...', 'Saving...') : t('Salvează', 'Save')}
          </button>
        </div>
      </div>

      {/* Quick stats (existing clients only) */}
      {!isNew && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#07090f] border border-white/10 rounded-xl px-4 py-3 text-center">
            <div className="text-lg font-bold text-[#4ab8ff]">{form.services.filter(s => s.status === 'active').length}</div>
            <div className="text-xs text-white/30 mt-0.5">{t('Servicii active', 'Active services')}</div>
          </div>
          <div className="bg-[#07090f] border border-white/10 rounded-xl px-4 py-3 text-center">
            <div className="text-lg font-bold text-white">{clientOrders.length}</div>
            <div className="text-xs text-white/30 mt-0.5">{t('Comenzi totale', 'Total orders')}</div>
          </div>
          <div className="bg-[#07090f] border border-white/10 rounded-xl px-4 py-3 text-center">
            <div className="text-lg font-bold text-green-400">{clientOrders.filter(o => o.status === 'completed').reduce((s, o) => s + o.total, 0).toLocaleString()} €</div>
            <div className="text-xs text-white/30 mt-0.5">Revenue total</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {([
          ['info', t('Informații', 'Information')],
          ['services', t('Servicii & Produse', 'Services & Products')],
          ['orders', t('Comenzi', 'Orders')],
        ] as const).map(([tKey, tLabel]) => (
          <button key={tKey} onClick={() => setTab(tKey)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${tab === tKey ? 'text-[#4ab8ff] border-[#295CA8]' : 'text-white/40 border-transparent hover:text-white'}`}>
            {tLabel}
            {tKey === 'services' && form.services.length > 0 && (
              <span className="ml-2 text-xs px-1.5 py-0.5 bg-[#295CA8]/30 text-[#4ab8ff] rounded-full">{form.services.length}</span>
            )}
            {tKey === 'orders' && clientOrders.length > 0 && (
              <span className="ml-2 text-xs px-1.5 py-0.5 bg-white/10 text-white/50 rounded-full">{clientOrders.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* INFO TAB */}
      {tab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-xs text-white/40 uppercase tracking-wide font-semibold">{t('Date contact', 'Contact details')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="block text-xs text-white/30 mb-1">{t('Nume complet', 'Full name')}</label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ionescu Alexandru" /></div>
                <div><label className="block text-xs text-white/30 mb-1">{t('Companie', 'Company')}</label><Input value={form.company} onChange={e => set('company', e.target.value)} placeholder="TechSoft SRL" /></div>
                <div><label className="block text-xs text-white/30 mb-1">CUI</label><Input value={form.cui} onChange={e => set('cui', e.target.value)} placeholder="RO12345678" /></div>
                <div><label className="block text-xs text-white/30 mb-1">Email</label><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="client@companie.ro" /></div>
                <div><label className="block text-xs text-white/30 mb-1">{t('Telefon', 'Phone')}</label><Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+40 7XX XXX XXX" /></div>
                <div><label className="block text-xs text-white/30 mb-1">{t('Adresă', 'Address')}</label><Input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Str. ..., București" /></div>
              </div>
            </div>
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
              <label className="block text-xs text-white/40 mb-2 uppercase tracking-wide">{t('Note interne', 'Internal notes')}</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors resize-none"
                placeholder={t('Note despre client...', 'Notes about client...')} />
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
              <label className="block text-xs text-white/40 mb-3 uppercase tracking-wide">Status</label>
              {(['active', 'inactive', 'prospect'] as const).map(s => (
                <button key={s} onClick={() => set('status', s)}
                  className={`w-full flex items-center gap-3 px-3 py-2 mb-1.5 rounded-lg border text-sm text-left transition-all ${form.status === s ? 'border-[#295CA8] bg-[#295CA8]/20' : 'border-white/10 hover:border-white/20'}`}>
                  <span className={`w-2 h-2 rounded-full ${s === 'active' ? 'bg-green-400' : s === 'inactive' ? 'bg-red-400' : 'bg-yellow-400'}`} />
                  <span className={form.status === s ? 'text-white' : 'text-white/50'}>
                    {s === 'active' ? t('Activ', 'Active') : s === 'inactive' ? t('Inactiv', 'Inactive') : t('Prospect', 'Prospect')}
                  </span>
                  {form.status === s && <span className="ml-auto text-[#4ab8ff] text-xs">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SERVICES TAB */}
      {tab === 'services' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={addService}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-2 rounded-lg text-sm transition-all">
              <Plus size={15} /> {t('Adaugă serviciu', 'Add service')}
            </button>
          </div>

          {form.services.length === 0 ? (
            <div className="py-12 text-center text-white/20 text-sm border border-dashed border-white/10 rounded-xl">
              <ShoppingBag size={28} className="mx-auto mb-2 opacity-30" />
              {t('Niciun serviciu înregistrat', 'No services registered')}
            </div>
          ) : (
            <div className="space-y-3">
              {form.services.map((s, i) => (
                <div key={i} className="bg-[#07090f] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                      {PROD_TYPE_LABELS[s.productType] || s.productType}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${s.status === 'active' ? 'bg-green-500/20 text-green-400' : s.status === 'suspended' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                        {s.status === 'active' ? t('Activ', 'Active') : s.status === 'suspended' ? t('Suspendat', 'Suspended') : t('Expirat', 'Expired')}
                      </span>
                      <button onClick={() => removeService(i)} className="p-1 rounded text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2"><label className="block text-xs text-white/30 mb-1">{t('Produs/Serviciu', 'Product/Service')}</label>
                      <input value={s.productName} onChange={e => updateService(i, 'productName', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors"
                        placeholder="Ex: Site Business, Hosting..." />
                    </div>
                    <div><label className="block text-xs text-white/30 mb-1">{t('Tip', 'Type')}</label>
                      <select value={s.productType} onChange={e => updateService(i, 'productType', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors">
                        {Object.entries(PROD_TYPE_LABELS).map(([k]) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </div>
                    <div><label className="block text-xs text-white/30 mb-1">{t('Data start', 'Start date')}</label>
                      <input type="date" value={s.startDate} onChange={e => updateService(i, 'startDate', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors" />
                    </div>
                    <div><label className="block text-xs text-white/30 mb-1">{t('Reînnoire', 'Renewal date')}</label>
                      <input type="date" value={s.renewalDate || ''} onChange={e => updateService(i, 'renewalDate', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors" />
                    </div>
                    <div><label className="block text-xs text-white/30 mb-1">Status</label>
                      <select value={s.status} onChange={e => updateService(i, 'status', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors">
                        <option value="active">{t('Activ', 'Active')}</option>
                        <option value="suspended">{t('Suspendat', 'Suspended')}</option>
                        <option value="expired">{t('Expirat', 'Expired')}</option>
                      </select>
                    </div>
                    <div className="md:col-span-3"><label className="block text-xs text-white/30 mb-1">{t('Note', 'Notes')}</label>
                      <input value={s.notes} onChange={e => updateService(i, 'notes', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors"
                        placeholder={t('Notă despre serviciu...', 'Service note...')} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ORDERS TAB */}
      {tab === 'orders' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Link to={`/orders/new`}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-2 rounded-lg text-sm transition-all">
              <Plus size={15} /> {t('Comandă nouă', 'New order')}
            </Link>
          </div>
          {clientOrders.length === 0 ? (
            <div className="py-12 text-center text-white/20 text-sm border border-dashed border-white/10 rounded-xl">
              <ShoppingBag size={28} className="mx-auto mb-2 opacity-30" />
              {t('Nicio comandă înregistrată', 'No orders recorded')}
            </div>
          ) : (
            clientOrders.map(o => (
              <Link key={o.id} to={`/orders/${o.id}`}
                className="flex items-center gap-4 bg-[#07090f] border border-white/10 hover:border-[#295CA8]/40 rounded-xl px-5 py-4 transition-all group">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-[#4ab8ff] font-semibold">{o.orderNumber}</span>
                    <span className={`text-xs font-medium ${ORDER_STATUS_COLORS[o.status]}`}>
                      {lang === 'ro' ? ORDER_STATUS_RO[o.status] : o.status}
                    </span>
                  </div>
                  <div className="text-xs text-white/30 mt-1">{o.items.map(i => i.productName).join(' · ')} — {o.createdAt}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-white">{o.total.toLocaleString()} {o.currency}</div>
                </div>
                <ExternalLink size={14} className="text-white/20 group-hover:text-[#4ab8ff] transition-colors flex-shrink-0" />
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
